import React, { useMemo, useState } from 'react';
import { IDynamicDiplayView } from '@components/widgets/dynamic-display';
import DynamicWidget from '@components/widgets/dynamic-display/DynamicWidget';
import { IProductionDetailsSummary } from '../../..';
import { EnhancedDisplayType } from '@components/widgets/dynamic-display/EnhancedDisplay';
import EnhancedButton from '@components/button/EnhancedButton';
import MarineDetailsDrawer from './marine-details-drawer/MarineDetailsDrawer';

interface IMarineDetailsWidgetProps {
  allowPolicyCorrection: boolean;
  data: IProductionDetailsSummary;
  lovs: Record<string, Record<string, string>>;
  onDataRefresh?: () => void;
}

const MarineDetailsWidget: React.FC<IMarineDetailsWidgetProps> = ({
  allowPolicyCorrection,
  data,
  lovs,
  onDataRefresh,
}) => {
  const [marineInfoDrawerOpen, setMarineInfoDrawerOpen] =
    useState<boolean>(false);

  const dynamicView = useMemo(
    (): IDynamicDiplayView => ({
      name: 'PolicyMarineDetails',
      sections: [
        {
          name: 'MarineDetails',
          title: 'Marine Details',
          properties: {
            matter: {
              multiline: false,
              value: data?.Marine?.matter,
              name: 'matter',
              title: 'Matter',
              fullLine: true,
            },
            description: {
              multiline: false,
              value: data?.Marine?.description,
              name: 'description',
              title: 'Description',
              fullLine: true,
            },
            vesselName: {
              multiline: false,
              value: data?.Marine?.vesselName,
              name: 'vesselName',
              title: 'Vessel Name',
            },
            vesselAge: {
              multiline: false,
              value: data?.Marine?.vesselAge?.toString(),
              name: 'vesselAge',
              title: 'Vessel Age',
            },
            from: {
              multiline: false,
              value: data?.Marine?.from,
              name: 'from',
              title: 'Departure From',
            },
            to: {
              multiline: false,
              value: data?.Marine?.to,
              name: 'to',
              title: 'Destination To',
            },
            via: {
              multiline: false,
              value: data?.Marine?.via,
              name: 'via',
              title: 'Transportation Via',
            },
            survey: {
              multiline: false,
              value: data?.Marine?.survey,
              name: 'survey',
              title: 'Survey',
            },
            cargoContract: {
              multiline: false,
              value: data?.Marine?.cargoContract,
              name: 'cargoContract',
              title: 'Cargo Contract',
            },
            sumPerShipmentCurrency: {
              multiline: false,
              value: data?.Marine?.sumPerShipmentCurrency,
              name: 'sumPerShipmentCurrency',
              title: 'Sum Per Shipment Currency',
            },
            sumPerShipment: {
              multiline: false,
              value: data?.Marine?.sumPerShipment?.toString(),
              name: 'sumPerShipment',
              title: 'Sum Per Shipment',
            },
            sumPerShipmentPolicyCurrency: {
              multiline: false,
              value: data?.Marine?.sumPerShipmentPolicyCurrency.toString(),
              name: 'sumPerShipmentPolicyCurrency',
              title: 'Sum Per Shipment In Policy Currency',
            },
            lcNumber: {
              multiline: false,
              value: data?.Marine?.lcNumber,
              name: 'lcNumber',
              title: 'LC Number',
            },
            lcIssueDate: {
              multiline: false,
              value: data?.Marine?.lcIssueDate,
              type: EnhancedDisplayType.Date,
              name: 'lcIssueDate',
              title: 'LC Issue Date',
            },
            bank: {
              multiline: false,
              value: data?.Marine?.bank,
              name: 'bank',
              title: 'Bank',
            },
            claimsPayableBy: {
              multiline: false,
              value: data?.Marine?.claimsPayableBy,
              name: 'claimsPayableBy',
              title: 'Claims Payable by',
            },
            loading: {
              multiline: false,
              value: data?.Marine?.loading?.toString(),
              type: EnhancedDisplayType.RoundedPercentage,
              name: 'loading',
              title: 'Loading',
            },
            remarks: {
              multiline: false,
              value: data?.Marine?.remarks,
              name: 'remarks',
              title: 'Remarks',
              fullLine: true,
            },
          },
        },
      ],
    }),
    [data]
  );

  return (
    <>
      <DynamicWidget
        hasFourFields={true}
        view={dynamicView}
        actions={
          allowPolicyCorrection && (
            <div>
              <EnhancedButton
                isPrimary
                onClick={() => {
                  setMarineInfoDrawerOpen(true);
                }}
              >
                Modify
              </EnhancedButton>
            </div>
          )
        }
      />
      {marineInfoDrawerOpen && (
        <MarineDetailsDrawer
          open={marineInfoDrawerOpen}
          onClose={() => setMarineInfoDrawerOpen(false)}
          onSuccess={() => {
            onDataRefresh?.();
          }}
          data={data}
          lovs={lovs}
        />
      )}
    </>
  );
};

export default MarineDetailsWidget;
